import { storeToRefs } from 'pinia';
import { computed, ComputedRef, Ref } from 'vue';
import { usePaymentSystemListStore } from '@/store/dictionary/payment-system-list.store';
import { TransactionType } from '@/models/common/transaction-type.model';
import { useSegmentListStore } from '@/store/dictionary/segment-list.store';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import { TagListParamsRequest } from '@/models/dictionary/tag-list.model';
import { useMultiWhitelabelListStore } from '@/store/multi-whitelabel-slug-list/multi-whitelabel-slug-list.store';
import { useTagExternalIdListStore } from '@/store/tag-external/tag-external-list.store';
import { useSegmentExternalIdListStore } from '@/store/segment-external/segment-external-list.store';
import { ExternalIdListItem, ExternalIdListItemIdList } from '@/models/external-id-list/external-id-list-item.model';
import {
  payoutOrderListFilterFormRef,
  usePayoutOrderListFilterStore,
} from '@/store/payout-order/payout-order-filter-list.store';
import { PayoutOrderListFilter } from '@/models/payout-order/payout-order-list-filter.model';
import { showErrorNotification } from '@/utils/show-notification.util';
import { useEnvStore } from '@/store/env.store';

type PayoutOrderListFilterComposables = {
  whiteLabelIdList: ComputedRef<string[]>;
  updateLoadedDictionaries: () => void;
  getExternalFilterIdList: () => Promise<ExternalFilterIdList>;
  getExternalFilterIdMap: () => Promise<ExternalFilterIdMap>;
  payoutOrderListFilterFormData: Ref<PayoutOrderListFilter>;
  submitAndValidateForm: () => Promise<boolean>;
}

type ExternalFilterIdList = {
  tagExternalIdList: ExternalIdListItem[];
  segmentExternalIdList: ExternalIdListItem[];
};

type ExternalFilterIdMap = {
  tagIdMap: Map<string, string[]>;
  segmentIdMap: Map<string, string[]>;
};

export const usePayoutOrderListFilter = (): PayoutOrderListFilterComposables => {
  const tagListStore = useTagListStore();
  const { isTagListNotEmpty } = storeToRefs(tagListStore);
  const { getTagList } = tagListStore;

  const segmentListStore = useSegmentListStore();
  const { isSegmentListNotEmpty } = storeToRefs(segmentListStore);
  const { getSegmentList } = segmentListStore;

  const paymentSystemListStore = usePaymentSystemListStore();
  const { isPaymentSystemListNotEmpty } = storeToRefs(paymentSystemListStore);
  const { getPaymentSystemList } = paymentSystemListStore;

  const { multiWhitelabelIdList } = storeToRefs(useMultiWhitelabelListStore());
  const { getTagExternalList } = useTagExternalIdListStore();
  const { getSegmentExternalList } = useSegmentExternalIdListStore();
  const { WHITELABEL_ID } = storeToRefs(useEnvStore());

  const {
    filterTagIdList,
    filterSegmentIdList,
    isFilterSegmentIdListNotEmpty,
    isFilterTagIdListNotEmpty,
    payoutOrderListFilterFormData,
  } = storeToRefs(usePayoutOrderListFilterStore());

  const whiteLabelIdList = computed<string[]>(() => [...multiWhitelabelIdList.value, WHITELABEL_ID.value]);

  const updateLoadedDictionaries = (): void => {
    const filterList = { wlIdList: whiteLabelIdList.value };

    if (isPaymentSystemListNotEmpty.value) {
      getPaymentSystemList({
        filterList: { ...filterList, paymentType: [TransactionType.WITHDRAW] },
        forceUpdate: true,
      });
    }
    if (isSegmentListNotEmpty.value) {
      getSegmentList({
        filterList: { ...filterList, isPublished: true },
        isForceUpdate: true,
      });
    }
    if (isTagListNotEmpty.value) {
      getTagList({
        object: TagListParamsRequest.PLAYER,
        filterList,
        isForceUpdate: true,
      });
    }
  };

  const getExternalFilterIdList = async (): Promise<ExternalFilterIdList> => {
    const [tagExternalIdList, segmentExternalIdList] = await Promise.all([
      isFilterTagIdListNotEmpty.value ? getTagExternalList({ tagId: filterTagIdList.value }) : [],
      isFilterSegmentIdListNotEmpty.value ? getSegmentExternalList({ segmentId: filterSegmentIdList.value }) : [],
    ]);

    return {
      tagExternalIdList,
      segmentExternalIdList,
    };
  };

  const createExternalIdMap = (externalIdList: ExternalIdListItem[]): Map<string, string[]> => {
    const externalIdMap = new Map<string, string[]>();

    externalIdList
      .flatMap(({ externalIds }: ExternalIdListItem) => externalIds)
      .forEach(({ wlSlug, id }: ExternalIdListItemIdList) => {
        const idList = externalIdMap.get(wlSlug) ?? [];

        idList.push(id);

        externalIdMap.set(wlSlug, idList);
      });

    return externalIdMap;
  };

  const getExternalFilterIdMap = async (): Promise<ExternalFilterIdMap> => {
    const { tagExternalIdList, segmentExternalIdList } = await getExternalFilterIdList();

    return {
      tagIdMap: createExternalIdMap(tagExternalIdList),
      segmentIdMap: createExternalIdMap(segmentExternalIdList),
    };
  };

  const submitAndValidateForm = async (): Promise<boolean> => {
    if (!payoutOrderListFilterFormRef.value) {
      showErrorNotification(new Error('Form is not ready'));

      return false;
    }

    try {
      await payoutOrderListFilterFormRef.value.validate();

      return true;
    } catch {
      showErrorNotification(
        new Error('Please properly fill all required fields.'),
      );

      return false;
    }
  };

  return {
    whiteLabelIdList,
    updateLoadedDictionaries,
    getExternalFilterIdList,
    getExternalFilterIdMap,
    payoutOrderListFilterFormData,
    submitAndValidateForm,
  };
};
